/**
 * Contest Page
 * Weekly writing contest: hero with prize + countdown to the week's end,
 * live top-10 leaderboard, how-it-works, and past winners.
 * Every response to a daily prompt this week (Mon 2am UK -> Mon 2am UK)
 * is automatically an entry.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { capture } from '../utils/analytics';
import './ContestPage.css';

/** weekKey is the Monday of the contest week (YYYY-MM-DD). */
const formatWeekLabel = (weekKey) => {
  const d = new Date(`${weekKey}T12:00:00Z`);
  return isNaN(d.getTime())
    ? weekKey
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const ContestPage = () => {
  const [current, setCurrent] = useState(null);
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    capture('contest_page_viewed');
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [cur, past] = await Promise.all([
          axios.get('/api/contest/current'),
          axios.get('/api/contest/winners')
        ]);
        setCurrent(cur.data);
        setWinners(past.data || []);
      } catch (error) {
        console.error('Error loading contest:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Inline countdown to the end of the contest week (same idea as the daily prompt timer).
  useEffect(() => {
    if (!current?.weekEnd) return undefined;
    const update = () => {
      const diff = new Date(current.weekEnd) - new Date();
      if (diff <= 0) {
        setTimeLeft('Wrapping up…');
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setTimeLeft(days > 0 ? `${days}d ${hours}h ${minutes}m` : `${hours}h ${minutes}m ${seconds}s`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [current?.weekEnd]);

  if (loading) {
    return <div className="container loading">Loading contest...</div>;
  }

  const leaderboard = current?.leaderboard || [];
  const pastWinners = winners.filter((w) => w.post);

  return (
    <div className="container">
      <div className="contest-hero">
        <h1>Weekly Writing Contest</h1>
        <p className="contest-hero-sub">
          Respond to any daily prompt this week and you're automatically in.
        </p>
        {current?.prizeAmount && (
          <p className="contest-prize">
            This week's prize: <strong>{current.prizeAmount}</strong>
          </p>
        )}
        {timeLeft && <p className="contest-countdown">Week ends in {timeLeft}</p>}
      </div>

      <section className="contest-section">
        <h2>This week's leaderboard</h2>
        {leaderboard.length === 0 ? (
          <div className="no-posts">
            <p>No entries yet this week — respond to today's prompt and claim the top spot.</p>
          </div>
        ) : (
          <ol className="contest-leaderboard">
            {leaderboard.map((entry, index) => (
              <li key={entry._id}>
                <Link to={`/post/${entry._id}`} className="contest-entry">
                  <span className="contest-rank">{index + 1}</span>
                  <div className="contest-entry-body">
                    <div className="contest-entry-author">
                      {entry.author?.fullName || entry.author?.username || 'A writer'}
                      {entry.promptText && (
                        <span className="contest-entry-prompt"> · {entry.promptText}</span>
                      )}
                    </div>
                    <div className="contest-entry-excerpt">{entry.excerpt}</div>
                  </div>
                  <span className="contest-likes">♥ {entry.likeCount}</span>
                </Link>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="contest-section">
        <h2>How it works</h2>
        <ul className="contest-how">
          <li>Write a response to any daily prompt this week — that's it, you're entered.</li>
          <li>Community likes shape the live leaderboard all week long.</li>
          <li>Every Monday one winner is picked on merit — the best writing, not just the most likes.</li>
        </ul>
        <p className="contest-terms">
          Free to enter · one winner each week · judged on quality, not just likes ·
          entrants must be 18+ or have a guardian's consent · the prize is paid directly by WriteFlow.
        </p>
      </section>

      <section className="contest-section">
        <h2>Past winners</h2>
        {pastWinners.length === 0 ? (
          <p className="contest-empty">No winners crowned yet — this could be you.</p>
        ) : (
          <ul className="contest-winners">
            {pastWinners.map((w) => (
              <li key={w._id}>
                <Link to={`/post/${w.post._id}`} className="contest-winner-row">
                  <span className="contest-trophy" aria-hidden="true">🏆</span>
                  <span className="contest-winner-week">Week of {formatWeekLabel(w.weekKey)}</span>
                  <span className="contest-winner-author">
                    {w.post.author?.fullName || w.post.author?.username || 'A writer'}
                  </span>
                  {w.prizeAmount && <span className="contest-winner-prize">{w.prizeAmount}</span>}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default ContestPage;
